import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Notification } from '@models/notifications.model';
import {
  OrderApproverData,
  OrderApproversResponse,
  SelectedOrderApprover
} from '@models/shopping.model';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { SelectApproversComponent } from '@shared-components/select-approvers/select-approvers.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ts-order-approvers',
  templateUrl: './order-approvers.component.html',
  imports: [CommonModule, ReactiveFormsModule, TranslationPipe, SelectApproversComponent]
})
export class OrderApproversComponent implements OnInit, OnChanges, OnDestroy {
  @Input() skip_approval: boolean = false;
  @Output() valid_approver_form: EventEmitter<boolean> = new EventEmitter();
  @Output() approval_notification: EventEmitter<Notification> = new EventEmitter();
  @Output() selected_approver_output: EventEmitter<SelectedOrderApprover[]> = new EventEmitter();
  form: FormGroup = new FormGroup({});
  order_id: number;
  approvers: OrderApproverData[] = [];
  approver_notification_body: string = '';
  missing_approvers: OrderApproverData[] = [];
  selected_approvers: SelectedOrderApprover[] = [];
  approver_missing_warning = false;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activated_route: ActivatedRoute,
    private shopping_svc: ShoppingService,
    private titlecase_pipe: TitleCasePipe,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.order_id = this.activated_route.snapshot.params['id'];
    this.subscriptions.add(
      this.shopping_svc
        .getOrderApprovers(this.order_id)
        .subscribe((res: OrderApproversResponse) => {
          if (!res.approvers.length) {
            this.valid_approver_form.emit(true);
            return;
          }

          this.approvers = res.approvers;
          this.approver_notification_body = res.notification;

          if (!this.skip_approval && this.approvers.length > 0)
            this.createAndEmitApproverNotification(this.approvers, this.approver_notification_body);

          if (this.missing_approvers.length === 0) this.valid_approver_form.emit(true);
        })
    );

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => this.valid_approver_form.emit(this.form.valid))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(): void {
    if (!this.skip_approval && this.approvers.length > 0)
      this.createAndEmitApproverNotification(this.approvers, this.approver_notification_body);

    if (this.order_id)
      this.subscriptions.add(
        this.shopping_svc.getOrderApprovers(this.order_id, this.skip_approval).subscribe({
          next: (res: OrderApproversResponse) => {
            this.approvers = res.approvers;
            this.approver_notification_body = res.notification;

            if (!this.skip_approval && this.approvers.length > 0)
              this.createAndEmitApproverNotification(
                this.approvers,
                this.approver_notification_body
              );

            if (
              !this.approvers.length ||
              this.skip_approval ||
              this.selected_approvers.length > 0 ||
              this.missing_approvers.length === 0
            ) {
              this.valid_approver_form.emit(true);
              return;
            } else this.valid_approver_form.emit(false);
          },
          error: (error: any) => {}
        })
      );

    if (
      !this.approvers.length ||
      this.skip_approval ||
      this.selected_approvers.length > 0 ||
      this.missing_approvers.length === 0
    )
      this.valid_approver_form.emit(true);
    else this.valid_approver_form.emit(false);
  }

  getControl(control: string): FormControl {
    return this.form.get(control) as FormControl;
  }

  selectApprover(event: NgbTypeaheadSelectItemEvent<any>, approver_id: number): void {
    let temp: SelectedOrderApprover = {
      id: approver_id,
      user_id: event.item.id,
      email: event.item.email
    };

    if (
      this.selected_approvers.find((approver: SelectedOrderApprover) => approver.id === approver_id)
    ) {
      this.selected_approvers = this.selected_approvers.filter(
        (approver: SelectedOrderApprover) => approver.id !== approver_id
      );
      this.selected_approvers.push(temp);
    } else this.selected_approvers.push(temp);

    this.selected_approver_output.emit(this.selected_approvers);
  }

  private isValidApproverValidator(): ValidatorFn {
    let valid: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const value: SelectedOrderApprover = control.value;

      if (!value) return null;

      return Boolean(value.id && value.user_id && value.email) ? null : { valid_approver: true };
    };

    return valid;
  }

  private createAndEmitApproverNotification(
    order_approvers: OrderApproverData[],
    notification: string
  ): void {
    this.approval_notification.emit({
      id: this.activated_route.snapshot.params['id'],
      subject: this.translation_svc.translate('approval_required'),
      notification: this.generateApproverNotificationBody(order_approvers, notification)
    });

    this.approvers.forEach((approver_data: OrderApproverData) => {
      if (
        !approver_data.approver &&
        approver_data.allow_selection &&
        order_approvers.filter((approver: OrderApproverData) => approver.allow_selection).length >
          this.missing_approvers.length
      ) {
        this.missing_approvers.push(approver_data);

        this.form.addControl(
          `selected_approver_${approver_data.id}`,
          new FormControl(null, [Validators.required, this.isValidApproverValidator])
        );
      }
    });
  }

  private generateApproverNotificationBody(
    approvers: OrderApproverData[],
    approver_notification: string
  ): string {
    let known_approver_list_string: string = '';
    approvers.forEach((approver_data: OrderApproverData) => {
      if (approver_data.approver) {
        known_approver_list_string += `<span class="d-block fw-light"> - ${approver_data.approver.first_name} ${approver_data.approver.last_name}
        (${approver_data.approver.email})</span>`;
      } else {
        if (approver_data.type === 'self_select_approver')
          known_approver_list_string += `<span class="d-block fw-light"> - ${this.translation_svc.translate(
            'self_selected_approver'
          )}</span>`;
        else if (!approver_data.allow_selection && approver_data.type === 'group_approval')
          known_approver_list_string += `<span class="d-block fw-light"> - ${approver_data.title}</span>`;
        else {
          known_approver_list_string += `<span class="d-block approver-not-found"> - ${this.titlecase_pipe.transform(
            approver_data.title
          )} (${this.translation_svc.translate('not_found')})</span>`;
          this.approver_missing_warning = true;
        }
      }
    });

    return [
      `<span class="d-block mb-2">${approver_notification}</span>`,
      `<span class="fw-normal">`,
      known_approver_list_string,
      `</span>`
    ].join('');
  }
}
